import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AuthPageContainer from './AuthPageContainer';
import { validateEmailToken } from 'api/userApi';
import { useMutation } from '@tanstack/react-query';

const EmailVerificationStatus = () => {
  const { token } = useParams();

  const {
    mutate: validateEmail,
    isLoading: isValidating,
    isError,
    isSuccess,
  } = useMutation(['validate-email', token], () => validateEmailToken(token));

  useEffect(() => {
    if (token) validateEmail();
  }, [token]);

  return (
    <AuthPageContainer
      title="Email Verification"
      subheader={
        isValidating
          ? 'Verifying your email...'
          : isSuccess
          ? 'Email Verified!'
          : isError
          ? 'Verification Failed!'
          : ''
      }
      isLoading={false}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        gap={2}>
        {isValidating && (
          <>
            <CircularProgress color="primary" size={48} />
            <Typography variant="body1">
              Please wait while we verify your email.
            </Typography>
          </>
        )}

        {isSuccess && (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: 'success.main' }}
            />
            <Typography variant="h6">Your email has been verified!</Typography>
            <Typography variant="body2" color="text.secondary">
              You can now log in to your account and start using Restorize.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/login"
              sx={{ mt: 2 }}>
              Go to Login
            </Button>
          </>
        )}

        {isError && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
            <Typography variant="h6">Verification Failed</Typography>
            <Typography variant="body2" color="text.secondary">
              The verification link is invalid or has expired.
            </Typography>
            <Divider sx={{ width: '100%', my: 2 }} />

            <Typography variant="body2">
              To resend the verification email,{' '}
              <Link to="/login" style={{ color: '#1976d2', fontWeight: 500 }}>
                log in
              </Link>{' '}
              using your organization credentials.
            </Typography>
          </>
        )}
      </Box>
    </AuthPageContainer>
  );
};

export default EmailVerificationStatus;
